import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import { toJS } from 'mobx';

import CookieStorageService from 'services/CookieStorageService';
import ModalService from 'services/ModalService';
import VideoAdService from 'services/adServices/VideoAdService';
import ChatStore from 'stores/ChatStore';
import ProfileStore from 'stores/ProfileStore';
import FirebaseStore from 'stores/FirebaseStore';
import TippingStore from 'stores/TippingStore';
import UserStore from 'stores/UserStore';
import CompanionAd from 'services/adServices/CompanionAdService/CompanionAd';
import SFWOverlay from 'components/SFWOverlay/SFWOverlay';
import resolveIsJinkPlayerShouldBeUsed from 'components/StreamPlayer/common/resolveIsJinkPlayerShouldBeUsed';
import BroadcasterStreamHidden from 'components/StreamHidden/StreamHidden';
import { ConditionalSmartxPlayer } from 'components/StreamPlayer';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import { CONTEXTUAL_GOLD_MODAL_AREAS } from 'utils/consts';
import trackingService from 'services/UITrackingService';

import styles from './DesktopStreaming.css';


// eslint-disable-next-line complexity
function DesktopStreaming(props) {
  const {
    embedProfileV2,
    height,
    history,
    user,
  } = props;

  const {
    eventMainStream,
    fullscreen,
    hideProfileStream,
    inCustomFullscreen,
    isPlayerBroadcastMode,
    profileName,
    realityShow,
    streamingSource,
    toggleHideProfileStream,
  } = ProfileStore;
  const {
    isAnotherUserInC2C,
    isCam2CamShow,
    isStreamPortrait,
  } = FirebaseStore;
  const { isChatShown } = ChatStore;
  const { WebRTCSource } = UserStore;

  const isPortraitMode = isStreamPortrait || fullscreen;
  const isTheatreMode = isPortraitMode && !isChatShown;
  const heightStyle = (!isTheatreMode && height) ? { maxHeight: '100%' } : {};
  const isSfw = user.user.getIn(['user', 'sfw']);
  const { realCountry } = user;
  const isGuest = user?.accessLevel < ACCESS_LEVEL_NUM.BASIC;
  const hideAds = isGuest && CookieStorageService.get('cam4-HideAds');

  const onGuestOpenGoldOverlay = useCallback(() => {
    ModalService.showModal(ModalService.MODAL_KEYS.REGISTRATION_GOLD_MODAL, {
      history,
      onClose: ModalService.hideModal,
      showRegistrationGoldModal: true,
    });
  }, [history]);

  const onOpenUpgradeToGoldModal = useCallback(() => {
    const isGuestBilling = user.accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;
    if (isGuestBilling || user.accessLevel === ACCESS_LEVEL_NUM.GUEST) {
      const modalProps = {
        area: CONTEXTUAL_GOLD_MODAL_AREAS.ADS,
        onMembershipClick: onGuestOpenGoldOverlay,
      };

      ModalService.showModal(
        ModalService.MODAL_KEYS.UPGRADE_TO_GOLD_MODAL,
        modalProps
      );
    } else {
      ModalService.showModal(
        ModalService.MODAL_KEYS.UPGRADE_TO_GOLD_MODAL,
        { area: CONTEXTUAL_GOLD_MODAL_AREAS.ADS }
      );
    }

    trackingService.emit('adRemove', { adType: 'video' });
  }, [onGuestOpenGoldOverlay, user.accessLevel]);

  const containerClassNames = classNames({
    [styles.videoContainer]: true,
    [styles.jinkVideoContainer]: resolveIsJinkPlayerShouldBeUsed(),
  });

  const shouldUseWebRTC = Boolean(
    isCam2CamShow &&
    WebRTCSource &&
    !isAnotherUserInC2C(user.username)
  );

  let source = streamingSource;
  if (shouldUseWebRTC) {
    source = toJS(WebRTCSource);
  }

  if (realityShow?.mainEvent) {
    source = { type: 'HLS', uri: eventMainStream };
  }

  const shouldShowVideoAds = Boolean(
    !hideAds &&
    !embedProfileV2 &&
    VideoAdService.shouldDisplayVideoAds(user, ProfileStore.broadcastStatus)
  );

  const renderCompanionAds = useMemo(() => {
    if (embedProfileV2) {
      return null;
    }

    const companionAdClasses = classNames({
      [styles.companionAd]: true,
      [styles.adFixed]: inCustomFullscreen,
    });

    return (
      <CompanionAd wrapperClassName={companionAdClasses} />
    );
  }, [embedProfileV2, inCustomFullscreen]);

  if (hideProfileStream && isPlayerBroadcastMode) {
    return (
      <BroadcasterStreamHidden onClick={toggleHideProfileStream} />
    );
  }

  return (
    <div className={containerClassNames}>
      <div
        className={`${styles.DesktopStreaming} ${
          isPortraitMode ? styles.DesktopStreamingPortrait : styles.DesktopStreamingLandscape
        }`}
        style={heightStyle}
      >
        {source && (
          <ConditionalSmartxPlayer
            forcePlayerResize={
              `${
                FirebaseStore.roomDataReady
              }${FirebaseStore.newShowsState
              }${TippingStore.isPerformerGoalVisible
              }${ProfileStore.broadcastStatus}`
            }
            fullscreen={fullscreen}
            isDesktop={true}
            isSfw={isSfw}
            muted={false}
            preventDoubleClick={true}
            profileName={profileName}
            shouldShowVideoAds={shouldShowVideoAds}
            showControls={true}
            showPlayPause={false}
            showSeekBar={false}
            source={source}
            streaming={true}
            upgradeToGold={onOpenUpgradeToGoldModal}
            user={user}
          />
        )}
        {isSfw && (
          <SFWOverlay realCountry={realCountry} />
        )}
      </div>
      {renderCompanionAds}
    </div>
  );
}

DesktopStreaming.propTypes = {
  history: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  embedProfileV2: PropTypes.bool,
  height: PropTypes.number,
};

DesktopStreaming.defaultProps = {
  embedProfileV2: false,
  height: undefined,
};

export default observer(DesktopStreaming);
